// Phase 3.15 (hh-der) — EditEquipmentScreen.
//
// Manual edit form for one piece of equipment: rename, change model / brand,
// relocate, delete. Ported from designproject/files/screens-forms.jsx
// (`EditEquipmentScreen`) — the design's inline RegulationEditor is out of
// scope here (regulation editing lives in the dedicated RegulationDiffScreen,
// hh-z24). Backend:
//   - PATCH  /api/v1/equipment/{id}        — name / brand / model / category
//   - POST   /api/v1/equipment/{id}/move   — relocate (hh-lqe)
//   - DELETE /api/v1/equipment/{id}        — behind a confirm dialog
//
// There is no GET /api/v1/equipment/{id}; the equipment is located by walking
// GET /api/v1/properties/tree — the same approach the `card` feature uses for
// single-item loads. The tree also feeds the location picker.
//
// hh-lqe: location is now editable. The location is chosen through the shared
// EquipmentPlacePicker — a collapsed-by-default tree of properties with nested
// rooms plus a selectable property-level node. On save, a changed location is
// persisted via the move endpoint — recorded as a reversible ChangeRecord.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Btn, resolveIconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { equipment, humanizeError } from '../../api';
import type {
  EquipmentNode,
  PropertyTreeNode,
} from '../../api/endpoints/properties.js';
import { usePropertyTree } from './usePropertyTree';
import { FormLabel, FormInput, FormFooter, FormError } from './formPrimitives';
import { EquipmentPlacePicker } from './EquipmentPlacePicker';
import type { EquipmentPlaceSelection } from './EquipmentPlacePicker';
import { IconPicker } from './IconPicker';

export interface EditEquipmentScreenProps {
  readonly router: RouterApi;
  readonly equipmentId: string;
}

interface LocatedEquipment {
  readonly node: EquipmentNode;
  /** Human-readable location, e.g. "Дача → Котельная" or "Дача". */
  readonly locationLabel: string;
  /** Current location anchor — a room or the property itself. */
  readonly location: EquipmentPlaceSelection;
}

/** Walk the tree to find the equipment node and derive its location. */
function locateEquipment(
  tree: readonly PropertyTreeNode[],
  equipmentId: string,
): LocatedEquipment | null {
  for (const property of tree) {
    for (const eq of property.equipment) {
      if (eq.id === equipmentId) {
        return {
          node: eq,
          locationLabel: property.name,
          location: { kind: 'property', id: property.id },
        };
      }
    }
    for (const room of property.rooms) {
      for (const eq of room.equipment) {
        if (eq.id === equipmentId) {
          return {
            node: eq,
            locationLabel: `${property.name} → ${room.name}`,
            location: { kind: 'room', id: room.id },
          };
        }
      }
    }
  }
  return null;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

export function EditEquipmentScreen({
  router,
  equipmentId,
}: EditEquipmentScreenProps): JSX.Element {
  const tree = usePropertyTree();
  const [name, setName] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [brand, setBrand] = useState<string>('');
  const [selectedLoc, setSelectedLoc] = useState<EquipmentPlaceSelection | null>(null);
  // null = авто-подбор иконки по категории/названию; строка = ручной выбор.
  const [icon, setIcon] = useState<string | null>(null);
  const [original, setOriginal] = useState<LocatedEquipment | null>(null);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the form once from the tree.
  useEffect(() => {
    if (tree.status !== 'ready' || hydrated) return;
    const found = locateEquipment(tree.properties, equipmentId);
    if (found !== null) {
      setName(found.node.name);
      setModel(found.node.model ?? '');
      setBrand(found.node.brand ?? '');
      setSelectedLoc(found.location);
      setIcon(found.node.icon ?? null);
      setOriginal(found);
    }
    setHydrated(true);
  }, [tree.status, tree.properties, equipmentId, hydrated]);

  const notFound = hydrated && original === null;

  // Авто-иконка для превью: подбор по категории + названию оборудования.
  const autoIcon = useMemo(
    () => resolveIconName('equipment', name, original?.node.category),
    [name, original],
  );

  const canSave = useMemo<boolean>(
    () => original !== null && name.trim().length > 0 && selectedLoc !== null && !pending,
    [original, name, selectedLoc, pending],
  );

  const back = (): void => router.back();

  const save = (): void => {
    if (original === null || name.trim().length === 0 || selectedLoc === null) return;
    setPending(true);
    setError(null);

    const trimmedName = name.trim();
    const trimmedModel = model.trim();
    const trimmedBrand = brand.trim();
    const target = selectedLoc;
    const locationChanged =
      target.kind !== original.location.kind || target.id !== original.location.id;
    const iconChanged = icon !== (original.node.icon ?? null);

    equipment
      .updateEquipment(equipmentId, {
        name: trimmedName,
        model: trimmedModel,
        brand: trimmedBrand,
        // icon шлём только при изменении: строка → ручная установка,
        // null → сброс на авто-подбор (контракт hh-345).
        ...(iconChanged ? { icon } : {}),
      })
      .then(() => {
        // Локацию переносим отдельным вызовом — только если она изменилась.
        if (!locationChanged) return undefined;
        return equipment.moveEquipment(
          equipmentId,
          target.kind === 'room' ? { roomId: target.id } : { propertyId: target.id },
        );
      })
      .then(() => {
        router.back();
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  const remove = (): void => {
    if (pending) return;
    const label = original?.node.name ?? 'оборудование';
    const ok = window.confirm(
      `Удалить «${label}»? Это действие можно откатить из истории.`,
    );
    if (!ok) return;
    setPending(true);
    setError(null);
    equipment
      .deleteEquipment(equipmentId)
      .then(() => {
        router.go('tree');
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="Править оборудование"
        subtitle="Имя, модель, бренд, место"
        onBack={back}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        {tree.status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
        )}
        {tree.status === 'error' && tree.error !== null && (
          <FormError message={tree.error} />
        )}
        {notFound && (
          <FormError message="Оборудование не найдено — возможно, уже удалено." />
        )}

        {tree.status === 'ready' && original !== null && (
          <>
            <FormLabel>Имя</FormLabel>
            <FormInput value={name} onChange={setName} disabled={pending} />

            <FormLabel style={{ marginTop: 18 }}>Модель</FormLabel>
            <FormInput
              value={model}
              onChange={setModel}
              placeholder="Например: WBN6000-18C"
              disabled={pending}
            />

            <FormLabel style={{ marginTop: 18 }}>Бренд</FormLabel>
            <FormInput
              value={brand}
              onChange={setBrand}
              placeholder="Например: Bosch"
              disabled={pending}
            />

            <div style={{ marginTop: 18 }}>
              <IconPicker
                value={icon}
                onChange={setIcon}
                autoIcon={autoIcon}
                disabled={pending}
              />
            </div>

            <FormLabel style={{ marginTop: 18 }}>Место</FormLabel>
            <EquipmentPlacePicker
              tree={tree.properties}
              value={selectedLoc}
              onChange={(sel) => {
                setSelectedLoc(sel);
                setError(null);
              }}
              disabled={pending}
            />

            <Btn
              kind="danger"
              icon="trash"
              onClick={remove}
              disabled={pending}
              style={{ marginTop: 18, width: '100%' }}
            >
              Удалить оборудование
            </Btn>

            {error !== null && <FormError message={error} />}
          </>
        )}
      </div>
      <FormFooter
        onCancel={back}
        onConfirm={save}
        confirmLabel="Сохранить"
        confirmIcon="check"
        disabled={!canSave}
        pending={pending}
      />
    </div>
  );
}
